<template>
  <div>
    <Modal
      class-name="department-modal"
      :title="getModalTitle"
      :saving="isSaving"
      :min-height="breakpoints().md ? 500 : 0"
      @hide-modal="$emit('hide-modal')"
      @submit="saveDepartment"
    >
      <warning-message
        v-if="currentDepartment?.archived"
        data-cy="archivedMessage"
        class="warning-banner"
      >
        <span>ARCHIVED DEPARTMENT</span>
      </warning-message>
      <DepartmentFields ref="departmentFields" />
      <LateFees v-show="lateFeesEnabled" ref="lateFeesFields" />
      <template #delete>
        <SeraButton
          v-if="currentDepartment?.links?.archive"
          data-cy="archiveButton"
          type="danger"
          text="Archive"
          @click="showArchiveConfirm"
        />
        <SeraButton
          v-else-if="currentDepartment?.links?.unarchive"
          data-cy="unarchiveButton"
          text="Unarchive"
          :saving="isUnarchiving"
          outline
          @click="handleUnarchive"
        />
      </template>
    </Modal>

    <!-- Archive Delete Confirmation -->
    <ArchiveDepartmentModal
      v-if="archiveConfirmVisible"
      :department="currentDepartment"
      @hide-modal="handleHideArchiveConfirm"
      @archived="refreshData"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

import SeraButton from "@/components/Button.vue";
import ArchiveDepartmentModal from "@/Department/ArchiveDepartmentModal.vue";
import useVuelidate from "@vuelidate/core";
import Modal from "../../components/Modal.vue";
import DepartmentFields from "./DepartmentFields.vue";
import LateFees from "./LateFeesFields.vue";

export default {
  components: {
    ArchiveDepartmentModal,
    SeraButton,
    Modal,
    LateFees,
    DepartmentFields
  },
  props: {
    departmentId: {
      type: [Number, String],
      default: null
    }
  },
  emits: ["hide-modal", "reload-departments"],
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  data() {
    return {
      loadingArchiveConfirmData: null,
      archiveConfirmResults: null,
      archiveConfirmVisible: false,
      isUnarchiving: false,
      isSaving: false,
      isEditing: false
    };
  },
  computed: {
    ...mapGetters(["showAccountingCategories", "showAccountingClasses"]),
    ...mapState(["currentDepartment"]),
    getModalTitle() {
      return this.isEditing ? "Edit Department" : "Create Department";
    },
    lateFeesEnabled() {
      return this.currentDepartment?.late_fees_enabled;
    }
  },
  mounted() {
    this.refreshData();
    if (this.showAccountingClasses) {
      this.fetchAccountingClasses();
    }
    if (this.showAccountingCategories) {
      this.fetchAccountingCategories();
    }
  },
  methods: {
    ...mapActions([
      "unarchiveDepartment",
      "fetchDepartment",
      "updateDepartment",
      "createDepartment",
      "fetchAccountingClasses",
      "fetchAccountingCategories"
    ]),
    async refreshData() {
      if (this.departmentId) {
        this.fetchDepartment(this.departmentId);
        this.isEditing = true;
      }
    },
    async showArchiveConfirm() {
      this.archiveConfirmVisible = true;
    },
    handleHideArchiveConfirm() {
      this.archiveConfirmVisible = false;
    },
    async handleUnarchive() {
      this.isUnarchiving = true;
      try {
        await this.unarchiveDepartment(this.currentDepartment);
        window.notify.success("Department has been unarchived.");
        this.refreshData();
      } catch (e) {
        console.error(e);
      }
      this.isUnarchiving = false;
    },
    saveDepartment() {
      this.getDataFromFields()
        .then((data) => {
          this.isSaving = true;
          const departmentAction = this.isEditing
            ? this.updateDepartment
            : this.createDepartment;
          departmentAction(data)
            .then(() => {
              this.closeModalOnFinish();
            })
            .catch(() => {
              this.isSaving = false;
            });
        })
        .catch(() => {});
    },
    getDataFromFields() {
      const promises = [this.$refs.departmentFields.getDataToSave()];
      if (this.lateFeesEnabled)
        promises.push(this.$refs.lateFeesFields?.getDataToSave());

      return new Promise((resolve, reject) => {
        Promise.all(promises)
          .then((values) => {
            const departmentFields = values[0];
            const lateFeeFields = values[1];
            resolve({
              id: this.departmentId,
              ...departmentFields,
              ...lateFeeFields
            });
          })
          .catch((e) => {
            reject(e);
          });
      });
    },
    closeModalOnFinish() {
      const action = this.isEditing ? "updated" : "created";
      window.notify.success(`Department has been ${action} successfully!`);
      this.isSaving = false;
      this.$emit("hide-modal");
      this.$emit("reload-departments");
    }
  }
};
</script>

<style lang="scss">
.summary {
  background: var(--light-gray);
  border: 1px solid var(--gray);
  border-radius: 5px;
  padding: 10px;
}

.row {
  display: block;
}

.half-row {
  width: 48%;
}
</style>
